/**
 * PublicListToggle
 *
 * Bare icon button that opens/closes the artifact list on the public share +
 * embed maps. Designed to live inside a `FloatingPanel` (which supplies the
 * frosted-glass pill and fixed positioning), mirroring the authenticated map's
 * `MobileSidebarToggle`. The host page decides where the panel sits.
 *
 * @module components/share/public/PublicListToggle
 */
import { memo } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { ICON_SIZE_LG } from '@/constants/ui';

interface PublicListToggleProps {
  /** Whether the list panel is currently open (drives `aria-pressed`). */
  opened: boolean;
  /** Toggle the list panel open/closed. */
  onToggle: () => void;
}

export const PublicListToggle = memo(function PublicListToggle({
  opened,
  onToggle,
}: PublicListToggleProps) {
  const { t } = useTranslation();

  // Mirror the authenticated map's SidePanelToggleButton: collapse glyph when the
  // list is open, expand glyph when closed.
  const Icon = opened
    ? IconLayoutSidebarLeftCollapse
    : IconLayoutSidebarLeftExpand;

  return (
    <Tooltip label={t('share.public.listToggle')} withArrow>
      <ActionIcon
        variant="subtle"
        onClick={onToggle}
        aria-label={t('share.public.listToggle')}
        aria-pressed={opened}
      >
        <Icon size={ICON_SIZE_LG} />
      </ActionIcon>
    </Tooltip>
  );
});
